<template>
    <div class="grafico-container">
        <div class="grafico-header">
            <h3 class="grafico-titulo">Tarefas Atrasadas por Responsável</h3>
            <GraficoInfoIcon 
                titulo="Tarefas Atrasadas por Responsável"
                descricao="Mostra a quantidade de tarefas atrasadas por cada responsável, destacando possíveis gargalos na execução dos projetos."
            />
        </div>
        <div v-if="!temDados" class="sem-dados">Nenhum dado disponível</div>
        <Bar v-else :data="chartData" :options="chartOptions" />
    </div>
</template>

<script>
import { Bar } from 'vue-chartjs';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend } from 'chart.js';
import GraficoInfoIcon from './GraficoInfoIcon.vue';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

export default {
    name: 'GraficoTarefasPorResponsavel',
    components: { Bar, GraficoInfoIcon },
    props: {
        projetos: {
            type: Array,
            default: () => []
        }
    },
    emits: ['clicar-projeto'],
    computed: {
        dadosPorResponsavel() {
            const responsaveisMap = {};
            
            this.projetos.forEach(projeto => {
                if (projeto.entregas && Array.isArray(projeto.entregas)) {
                    projeto.entregas.forEach(entrega => {
                        if (entrega.sprints && Array.isArray(entrega.sprints)) {
                            entrega.sprints.forEach(sprint => {
                                if (sprint.tarefas && Array.isArray(sprint.tarefas)) {
                                    sprint.tarefas.forEach(tarefa => {
                                        const hoje = new Date();
                                        hoje.setHours(0, 0, 0, 0);
                                        const dtFim = tarefa.dtFim ? new Date(tarefa.dtFim) : null;
                                        const estaAtrasada = dtFim && dtFim < hoje && tarefa.status !== 'Concluído' && tarefa.status !== 'Cancelada' && !tarefa.dtFimReal;
                                        
                                        if (estaAtrasada && tarefa.responsavel) {
                                            const nome = tarefa.responsavel.name || 'Sem responsável';
                                            if (!responsaveisMap[nome]) {
                                                responsaveisMap[nome] = { nome, atrasadas: 0, total: 0 };
                                            }
                                            responsaveisMap[nome].atrasadas++;
                                            responsaveisMap[nome].total++;
                                        } else if (tarefa.responsavel) {
                                            const nome = tarefa.responsavel.name || 'Sem responsável';
                                            if (!responsaveisMap[nome]) {
                                                responsaveisMap[nome] = { nome, atrasadas: 0, total: 0 };
                                            }
                                            responsaveisMap[nome].total++;
                                        }
                                    });
                                }
                            });
                        }
                    });
                }
            });
            
            return Object.values(responsaveisMap)
                .filter(dados => dados && dados.atrasadas > 0)
                .sort((a, b) => b.atrasadas - a.atrasadas)
                .slice(0, 15);
        },
        temDados() {
            return this.dadosPorResponsavel.length > 0;
        },
        chartData() {
            const dados = this.dadosPorResponsavel;
            return {
                labels: dados.map(d => d.nome.length > 20 ? d.nome.substring(0, 20) + '...' : d.nome),
                datasets: [
                    {
                        label: 'Tarefas Atrasadas',
                        data: dados.map(d => d.atrasadas),
                        backgroundColor: 'rgba(231, 76, 60, 0.6)',
                        borderColor: 'rgba(231, 76, 60, 1)',
                        borderWidth: 2
                    },
                    {
                        label: 'Total de Tarefas',
                        data: dados.map(d => d.total),
                        backgroundColor: 'rgba(149, 165, 166, 0.4)',
                        borderColor: 'rgba(149, 165, 166, 1)',
                        borderWidth: 2
                    }
                ]
            };
        },
        chartOptions() {
            return {
                responsive: true,
                maintainAspectRatio: false,
                indexAxis: 'y',
                plugins: {
                    legend: {
                        display: true,
                        position: 'top'
                    },
                    tooltip: {
                        callbacks: {
                            label: (context) => {
                                const label = context.dataset.label || '';
                                const value = context.parsed.x;
                                return `${label}: ${value}`;
                            }
                        }
                    }
                },
                scales: {
                    x: {
                        beginAtZero: true,
                        stacked: false,
                        title: {
                            display: true,
                            text: 'Quantidade de Tarefas'
                        }
                    },
                    y: {
                        stacked: false,
                        title: {
                            display: true,
                            text: 'Responsáveis'
                        }
                    }
                }
            };
        }
    }
};
</script>

<style scoped>
.grafico-container {
    background: var(--cor-bg, white);
    border: 1px solid var(--cor-separador, #ddd);
    border-radius: 8px;
    padding: 1.5rem;
    height: 600px;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    box-sizing: border-box;
}

.grafico-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1rem;
}

.grafico-titulo {
    margin: 0;
    font-size: 1.2rem;
    font-weight: 600;
    color: var(--cor-fonte, #333);
}

.sem-dados {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #999;
}
</style>

